import type { FeeAdjustmentLineDisplay, FeeAdjustmentPricing } from "../types";
import { feeMoneyCents, feeMoneyCompactText, feeMoneyText, formatFeeAdjustmentAmount, formatFeeAdjustmentCompactAmount } from "./feeAdjustmentMoney";

export type FeeAdjustmentDescriptionPart = { text: string; direction: "increase" | "decrease" | "neutral" };

/** 变化方向只由当前维度的前后差额决定。 */
function part(text: string, delta = 0n): FeeAdjustmentDescriptionPart {
  return { text, direction: delta > 0n ? "increase" : delta < 0n ? "decrease" : "neutral" };
}

type ServiceKind = "stay" | "traffic" | "bus" | "point" | "meal" | "guide" | "unknown";

/** 只用结构化资源类别识别六类服务，不读取项目名称猜测。 */
function kindOf(line: FeeAdjustmentLineDisplay): ServiceKind {
  const aliases: Record<string, ServiceKind> = {
    stay: "stay", 住宿: "stay", big_transport: "traffic", traffic: "traffic", 大交通: "traffic",
    bus: "bus", charter: "bus", 包车: "bus", point: "point", ticket: "point", enterprise: "point", intangible: "point", 考察点: "point",
    meal: "meal", 餐: "meal", guide: "guide", 导游: "guide",
  };
  return aliases[line.resource_type] ?? "unknown";
}

/** 保留数量精度，以万分之一单位比较，拒绝缺失或负的服务乘数。 */
function countUnits(value: string | null | undefined): bigint | null {
  if (typeof value !== "string" || !/^\d{1,16}(?:\.\d{1,4})?$/u.test(value.trim())) return null;
  const [whole, fraction = ""] = value.trim().split(".");
  return BigInt(whole) * 10000n + BigInt(fraction.padEnd(4, "0"));
}

/** 整数数量省略小数，非整数至少两位且保留有效精度。 */
function countText(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  const fraction = String(absolute % 10000n).padStart(4, "0");
  if (fraction === "0000") return String(absolute / 10000n);
  return `${absolute / 10000n}.${fraction.replace(/0+$/, "").padEnd(2, "0")}`;
}

/** 子类决定服务动作，无法证明交通方式时保留服务泛称。 */
function activity(line: FeeAdjustmentLineDisplay): string {
  const entry = line.resource_entry_type;
  if (kindOf(line) === "traffic") {
    if (["train", "train_ticket", "railway"].includes(entry ?? "")) return "乘车";
    if (["flight", "flight_ticket", "airplane"].includes(entry ?? "")) return "乘机";
    return "服务";
  }
  if (line.fee_category === "enterprise") return "参访";
  if (line.fee_category === "intangible") return "体验";
  if (line.fee_category === "ticket") return "入场";
  return "服务";
}

/** 数量动作和量词始终服从实际单位。 */
function quantityWords(line: FeeAdjustmentLineDisplay, unit: string): { noun: string; decrease: string; generic: boolean } {
  const kind = kindOf(line);
  if (kind === "stay" && unit === "间") return { noun: "间房", decrease: "退", generic: false };
  if (kind === "traffic" && unit === "张") return { noun: activity(line) === "乘机" ? "张机票" : "张票", decrease: "退", generic: false };
  if (kind === "bus" && unit === "辆") return { noun: "辆车", decrease: "减少", generic: false };
  if (kind === "meal" && ["桌", "人", "份"].includes(unit)) return { noun: unit === "人" ? "人用餐" : `${unit}餐`, decrease: "减少", generic: false };
  if (kind === "guide" && unit === "人") return { noun: "名导游", decrease: "减少", generic: false };
  if (kind === "point" && line.fee_category === "ticket" && unit === "张") return { noun: "张门票", decrease: "退", generic: false };
  if (kind === "point" && activity(line) !== "服务" && unit === "人") return { noun: `人${activity(line)}`, decrease: "减少", generic: false };
  return { noun: unit, decrease: "减少", generic: true };
}

/** 规格按实际晚数、天数或次数表达，不使用出钱人数替代。 */
function billingWords(line: FeeAdjustmentLineDisplay, pricing = line.after): { label: string; increase: string; decrease: string; initial: string } {
  const kind = kindOf(line);
  const unit = pricing.spec_unit;
  if (kind === "stay" && unit === "晚") return { label: "晚", increase: "多住", decrease: "少住", initial: "住" };
  const verb = kind === "bus" ? "用车" : kind === "guide" ? "导游服务" : kind === "meal" ? pricing.unit === "份" ? "供餐" : "用餐" : activity(line);
  return { label: `${unit}${verb}`, increase: "增加", decrease: "减少", initial: kind === "guide" ? "服务" : verb };
}

/** 文字规格无数学正负，双侧快照可比时描述目标规格。 */
function specText(line: FeeAdjustmentLineDisplay): string | null {
  if (line.before_item_spec == null && line.after_item_spec == null) return null;
  if (line.before_item_spec == null || line.after_item_spec == null) return "历史规格缺失";
  if (line.before_item_spec === line.after_item_spec) return null;
  const labels: Record<ServiceKind, string> = {
    stay: "房型", traffic: activity(line) === "乘机" ? "舱等" : activity(line) === "乘车" ? "席别" : "交通规格",
    bus: "车型", point: activity(line) === "参访" ? "参访项目" : activity(line) === "体验" ? "体验项目" : "服务规格",
    meal: "餐标", guide: "导游规格", unknown: "规格",
  };
  return line.after_item_spec.trim() ? `${labels[kindOf(line)]}改为${line.after_item_spec}` : "规格文字已清空";
}

/** 单价标题来自服务类别，金额只取前后服务单价差额。 */
function priceLabel(line: FeeAdjustmentLineDisplay): string {
  const kind = kindOf(line);
  if (kind === "traffic") return "票价单价";
  if (kind === "bus") return "包车单价";
  if (kind === "meal") return "餐费单价";
  if (kind === "guide") return "导游单价";
  if (kind === "point" && activity(line) !== "服务") return `${activity(line) === "入场" ? "门票" : activity(line)}单价`;
  return "单价";
}

/** 缺字段和单位变化提示保持在该数量维度的固定位置。 */
function countChange(line: FeeAdjustmentLineDisplay, before: FeeAdjustmentPricing, field: "quantity" | "billing_count"): FeeAdjustmentDescriptionPart | null {
  const quantity = field === "quantity";
  const label = quantity ? "数量" : "规格";
  const unitField = quantity ? "unit" : "spec_unit";
  const previous = countUnits(before[field]);
  const current = countUnits(line.after[field]);
  if (previous === null) return part(`历史${label}缺失`);
  if (current === null) return part(`${label}信息不完整`);
  if (!before[unitField] || !line.after[unitField]) return part(`${label}计量口径待确认`);
  if (before[unitField] !== line.after[unitField]) return part(`${label}单位改为${line.after[unitField]}`);
  const delta = current - previous;
  if (delta === 0n) return null;
  if (!quantity) {
    const words = billingWords(line);
    return part(`${delta > 0n ? words.increase : words.decrease} ${countText(delta)} ${words.label}`, delta);
  }
  const words = quantityWords(line, line.after.unit);
  return part(`${words.generic ? "数量" : ""}${delta > 0n ? "增加" : words.decrease} ${countText(delta)} ${words.noun}`, delta);
}

/** 补齐未变化的服务乘数，全退时明确采用历史计价依据。 */
function countBasis(line: FeeAdjustmentLineDisplay, pricing: FeeAdjustmentPricing, field: "quantity" | "billing_count", historical = false): FeeAdjustmentDescriptionPart {
  const quantity = field === "quantity";
  const count = countUnits(pricing[field]);
  const unit = quantity ? pricing.unit : pricing.spec_unit;
  const label = quantity ? "数量" : "规格";
  if (count === null) return part(historical ? `历史${label}缺失` : `${label}信息不完整`);
  if (!unit) return part(`${label}计量口径待确认`);
  return part(`${historical ? "原" : ""}${quantity ? `${countText(count)} ${quantityWords(line, unit).noun}` : `${billingWords(line, pricing).initial} ${countText(count)} ${unit}`}`);
}

/** 展示真实单价及数量、规格单位，不从总额或出钱人数反推单价。 */
function priceBasis(line: FeeAdjustmentLineDisplay, pricing: FeeAdjustmentPricing, historical = false, moneyText = feeMoneyText): FeeAdjustmentDescriptionPart {
  const price = feeMoneyCents(pricing.unit_price_amount);
  if (price === null) return part(historical ? "历史单价缺失" : "单价信息不完整");
  if (!pricing.unit || !pricing.spec_unit || !pricing.pricing_unit_type) return part("单价计量口径待确认");
  return part(`${historical ? "原" : ""}${priceLabel(line)} ${moneyText(price)} 元/${pricing.unit}/${pricing.spec_unit}`);
}

/** 按数量、规格数/文字规格、单价展示完整计价维度，变化维度保留差额动作。 */
export function formatFeeAdjustmentDescriptionParts(line: FeeAdjustmentLineDisplay, compactAmounts = false, groupAmounts = false, formatAmount?: (value: string | null | undefined, signed?: boolean) => string): FeeAdjustmentDescriptionPart[] {
  const moneyText = formatAmount ? (cents: bigint) => formatAmount(feeMoneyText(cents), false).replace("¥", "") : groupAmounts
    ? (cents: bigint) => (compactAmounts ? formatFeeAdjustmentCompactAmount : formatFeeAdjustmentAmount)(feeMoneyText(cents), false).replace("¥", "")
    : compactAmounts ? feeMoneyCompactText : feeMoneyText;
  const after = line.after;
  if (line.adjustment_type === "add_service") {
    const count = countUnits(after.quantity);
    const billing = countUnits(after.billing_count);
    const quantity = count === null || !after.unit ? "新增服务（数量待确认）" : `新增 ${countText(count)} ${quantityWords(line, after.unit).noun}`;
    const spec = billing === null || !after.spec_unit ? "规格信息不完整" : `${billingWords(line).initial} ${countText(billing)} ${after.spec_unit}`;
    return [part(quantity, count ?? 0n), part(spec), priceBasis(line, after, false, moneyText)];
  }
  if (!line.before) return [part("历史调整信息缺失")];
  const before = line.before;
  const q = countChange(line, before, "quantity");
  // 全退按原服务退款，不能让目标晚数或单价覆盖历史依据。
  if (countUnits(after.quantity) === 0n && (countUnits(before.quantity) ?? 0n) > 0n && before.unit && before.unit === after.unit) return [q ?? part("数量信息不完整"), countBasis(line, before, "billing_count", true), priceBasis(line, before, true, moneyText)];
  const spec = specText(line);
  const billing = countChange(line, before, "billing_count");
  const parts = [q, billing, spec ? part(spec) : null];
  const oldPrice = feeMoneyCents(before.unit_price_amount);
  const newPrice = feeMoneyCents(after.unit_price_amount);
  if (oldPrice === null) parts.push(part("历史单价缺失"));
  else if (newPrice === null) parts.push(part("单价信息不完整"));
  else if (!before.pricing_unit_type || !after.pricing_unit_type || !before.unit || !after.unit || !before.spec_unit || !after.spec_unit || before.pricing_unit_type !== after.pricing_unit_type || before.unit !== after.unit || before.spec_unit !== after.spec_unit) parts.push(part("单价计量口径待确认"));
  else if (newPrice !== oldPrice) parts.push(part(`${priceLabel(line)}${newPrice > oldPrice ? "增加" : "减少"} ${moneyText(newPrice > oldPrice ? newPrice - oldPrice : oldPrice - newPrice)} 元`, newPrice - oldPrice));
  const changes = parts.filter((value): value is FeeAdjustmentDescriptionPart => value !== null);
  if (!changes.length) return [part("无调整")];
  parts[0] ??= countBasis(line, after, "quantity");
  parts[1] ??= countBasis(line, after, "billing_count");
  if (parts.length === 3) parts.push(priceBasis(line, after, false, moneyText));
  return parts.filter((value): value is FeeAdjustmentDescriptionPart => value !== null);
}

/** 保留纯文本出口，导出和其他调用方沿用同一描述规则。 */
export function formatFeeAdjustmentDescription(line: FeeAdjustmentLineDisplay): string {
  return formatFeeAdjustmentDescriptionParts(line).map((value) => value.text).join("，");
}
